import type { ReactNode } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type InterstitialTemplate =
  | 'statistics'
  | 'linear-progress'
  | 'circular-progress'
  | 'progress'
  | 'multi-testimonial'
  | 'performance-chart'
  | 'product-info'
  | 'product-formulation'
  | 'doctor'
  | 'state-availability'
  | 'sleep-stats'
  | 'welcome-steps'
  | 'results-loader';

// Template-specific data interfaces
interface ProgressTemplateData {
  percent: number;
  description: ReactNode | string;
}

interface TestimonialTemplateData {
  headline: ReactNode;
  imageUrl: string;
  calloutText?: ReactNode;
  disclaimer: ReactNode;
}

interface TestimonialAltTemplateData {
  headline: ReactNode;
  imageUrl: string;
  calloutText?: ReactNode;
  disclaimer: ReactNode;
}

interface LinearProgressTemplateData {
  percent: number;
  headline: ReactNode;
  supportingText: ReactNode;
}

interface CircularProgressTemplateData {
  percent: number;
  headline?: ReactNode; // Made optional
  subtext?: ReactNode; // Text under the progress
}

interface MultiTestimonialTemplateData {
  headline: ReactNode;
  testimonials: Array<{
    stars: number;
    quote: string;
    author: string;
  }>;
  buttonLabel: string;
}

interface PerformanceChartTemplateData {
  headline: ReactNode;
  subtitle?: ReactNode;
  disclaimer?: ReactNode;
  buttonLabel: string;
  imageUrl?: string;
}

interface ProductInfoTemplateData {
  headline: ReactNode;
  subtitle?: ReactNode;
  disclaimer?: ReactNode;
  footnote?: string;
  buttonLabel: string;
  imageUrl?: string;
}

interface ProductFormulationTemplateData {
  headline: ReactNode;
  imageUrl?: string;
  desktopImageUrl?: string;
  disclaimer?: ReactNode;
}

interface DoctorTemplateData {
  headline: ReactNode;
  disclaimer?: ReactNode;
  imageUrl?: string;
  desktopImageUrl?: string;
  doctorName: string;
  doctorTitle: string;
}

interface StateAvailabilityTemplateData {
  stateName: string;
  shippingInfo: ReactNode;
  buttonLabel: string;
  imageUrl?: string;
}

interface StatisticsTemplateData {
  headline: ReactNode;
  imageUrl: string;
  disclaimer: ReactNode;
}

interface SleepStatsTemplateData {
  headline: ReactNode;
  imageUrl?: string;
  desktopImageUrl?: string;
  disclaimer?: ReactNode;
}

interface WelcomeStepsTemplateData {
  steps: stepsArr[] | undefined;
  title: string | undefined;
}
interface ResultsLoaderTemplateData {
  test: string;
}

// Union type for all template data
export type TemplateData =
  | { type: 'progress'; data: ProgressTemplateData }
  | { type: 'testimonial'; data: TestimonialTemplateData }
  | { type: 'testimonial-alt'; data: TestimonialAltTemplateData }
  | { type: 'linear-progress'; data: LinearProgressTemplateData }
  | { type: 'circular-progress'; data: CircularProgressTemplateData }
  | { type: 'circular-progress-with-welcome-steps'; data: CircularProgressTemplateData }
  | { type: 'multi-testimonial'; data: MultiTestimonialTemplateData }
  | { type: 'performance-chart'; data: PerformanceChartTemplateData }
  | { type: 'product-info'; data: ProductInfoTemplateData }
  | { type: 'product-formulation'; data: ProductFormulationTemplateData }
  | { type: 'doctor'; data: DoctorTemplateData }
  | { type: 'state-availability'; data: StateAvailabilityTemplateData }
  | { type: 'statistics'; data: StatisticsTemplateData }
  | { type: 'statistics-alt'; data: StatisticsTemplateData }
  | { type: 'sleep-stats'; data: SleepStatsTemplateData }
  | { type: 'results-loader'; data: ResultsLoaderTemplateData }
  | { type: 'welcome-steps'; data: WelcomeStepsTemplateData };

interface InterstitialChain {
  currentStep: string;
  nextSteps: Record<string, string>; // Maps interstitial type to next path
  finalDestination: string;
  chainId: string; // Unique identifier for the chain
}

interface InterstitialState {
  nextPath: string | null;
  duration: number;
  template: InterstitialTemplate;
  templateData: TemplateData | null;
  isTransitioning: boolean;
  savedInterstitials: Record<
    string,
    {
      template: InterstitialTemplate;
      templateData: TemplateData;
      nextPath: string;
      duration: number;
    }
  >;
  // Chain management
  currentChain: InterstitialChain | null;
  setInterstitial: (params: {
    nextPath: string;
    duration?: number;
    template: InterstitialTemplate;
    templateData: TemplateData;
  }) => void;
  clear: () => void;
  setTransitioning: (isTransitioning: boolean) => void;
  saveQuestionInterstitial: (
    questionId: string,
    data: {
      template: InterstitialTemplate;
      templateData: TemplateData;
      nextPath: string;
      duration: number;
    },
  ) => void;
  getQuestionInterstitial: (questionId: string) => {
    template: InterstitialTemplate;
    templateData: TemplateData;
    nextPath: string;
    duration: number;
  } | null;
  // Chain management methods
  startChain: (
    chainId: string,
    finalDestination: string,
    initialSteps: Record<string, string>,
  ) => void;
  getNextPath: (interstitialType: string) => string;
  getCurrentChain: () => InterstitialChain | null;
  clearChain: () => void;
}

export const useInterstitialStore = create<InterstitialState>()(
  persist(
    (set, get) => ({
      nextPath: null,
      duration: 60,
      template: 'circular-progress',
      templateData: null,
      isTransitioning: false,
      savedInterstitials: {},
      currentChain: null,
      setInterstitial: ({ nextPath, duration = 60, template, templateData }) =>
        set({
          nextPath,
          duration,
          template,
          templateData,
          isTransitioning: false,
        }),
      clear: () =>
        set({
          nextPath: null,
          duration: 60,
          template: 'circular-progress',
          templateData: null,
          isTransitioning: false,
          currentChain: null,
        }),
      setTransitioning: (isTransitioning: boolean) =>
        set({
          isTransitioning,
        }),
      saveQuestionInterstitial: (questionId: string, data) =>
        set((state) => ({
          savedInterstitials: {
            ...state.savedInterstitials,
            [questionId]: data,
          },
        })),
      getQuestionInterstitial: (questionId: string) => {
        const state = get();
        return state.savedInterstitials[questionId] || null;
      },
      startChain: (
        chainId: string,
        finalDestination: string,
        initialSteps: Record<string, string>,
      ) =>
        set({
          currentChain: {
            currentStep: Object.keys(initialSteps)[0],
            nextSteps: initialSteps,
            finalDestination,
            chainId,
          },
        }),
      getNextPath: (interstitialType: string) => {
        const chain = get().currentChain;
        if (!chain) return '';
        const nextStep = chain.nextSteps[interstitialType];
        if (nextStep) {
          return nextStep;
        }
        return '';
      },
      getCurrentChain: () => get().currentChain,
      clearChain: () => set({ currentChain: null }),
    }),
    {
      name: 'interstitial-storage', // required: unique name
      partialize: (state) => ({
        nextPath: state.nextPath,
        duration: state.duration,
        template: state.template,
        // Don't persist templateData as it contains React elements
        isTransitioning: state.isTransitioning,
        savedInterstitials: state.savedInterstitials,
        currentChain: state.currentChain,
      }),
    },
  ),
);
